import { customerApiClient } from './customer-api-client';
import type {
  CustomerAuthorizationCaseDetail,
  CustomerAuthorizationCaseListData,
  CustomerAuthorizationCaseListQuery,
  RespondCustomerAuthorizationPayload,
} from '@/types/customer-authorization.types';

/** Reads the server-provided download filename from Content-Disposition. */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match?.[1] ?? fallback;
}

export const customerAuthorizationService = {
  list: (query: CustomerAuthorizationCaseListQuery): Promise<CustomerAuthorizationCaseListData> =>
    customerApiClient
      .get<CustomerAuthorizationCaseListData>('/customer-portal/authorization-cases', {
        params: query,
      })
      .then((r) => r.data),

  getById: (id: string): Promise<CustomerAuthorizationCaseDetail> =>
    customerApiClient
      .get<CustomerAuthorizationCaseDetail>(`/customer-portal/authorization-cases/${id}`)
      .then((r) => r.data),

  respond: (
    id: string,
    payload: RespondCustomerAuthorizationPayload,
  ): Promise<CustomerAuthorizationCaseDetail> =>
    customerApiClient
      .post<CustomerAuthorizationCaseDetail>(
        `/customer-portal/authorization-cases/${id}/respond`,
        payload,
      )
      .then((r) => r.data),

  /**
   * Phase 7 Slice 5B — the quotation document of the case's CURRENT sent revision,
   * server-mediated only (D-128 Q-4). The bytes are streamed through the
   * authenticated customerApiClient (a plain <a href> would not carry the Bearer
   * token, and the server never hands out a storage or signed URL to a customer).
   * The object URL created here is local to this browser and revoked at once —
   * it is not, and never becomes, a storage URL.
   */
  async downloadQuotationDocument(id: string): Promise<void> {
    const response = await customerApiClient.get(
      `/customer-portal/authorization-cases/${id}/quotation/document`,
      { responseType: 'blob' },
    );
    const filename = filenameFromDisposition(
      response.headers?.['content-disposition'] as string | undefined,
      'quotation',
    );
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
